import React from 'react';
import {
  Box,
  Chip,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { MAP_UI } from '../theme/mapViewUi';

export const mapSelectionDialogPaperProps = {
  sx: {
    borderRadius: 3,
    overflow: 'hidden',
    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.12)',
    border: '1px solid',
    borderColor: 'divider',
  },
};

export const mapSelectionDialogTabsSx = {
  minHeight: 44,
  px: 2,
  pt: 1,
  bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'),
  borderBottom: '1px solid',
  borderColor: 'divider',
  '& .MuiTabs-flexContainer': {
    gap: 0.5,
    pb: 1,
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.8125rem',
    letterSpacing: 0.1,
    minHeight: 34,
    px: 1.5,
    py: 0.5,
    borderRadius: 1.5,
    color: 'text.secondary',
    minWidth: 'auto',
    transition: 'background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
  },
  '& .MuiTab-root.Mui-selected': {
    color: MAP_UI.accentDark,
    bgcolor: 'background.paper',
    boxShadow: '0 1px 4px rgba(15, 23, 42, 0.08)',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTabs-scrollButtons': {
    width: 28,
    '&.Mui-disabled': { opacity: 0.25 },
  },
};

export function MapSelectionDialogTitle({ title = 'Map selection', subtitle }) {
  return (
    <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1 }}>
      <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.3 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}
    </DialogTitle>
  );
}

export function MapSelectionDialogTabs({ value, onChange, tabs }) {
  return (
    <Tabs
      value={value}
      onChange={onChange}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      sx={mapSelectionDialogTabsSx}
    >
      {tabs.map((tab) => (
        <Tab key={tab.label} label={tab.label} />
      ))}
    </Tabs>
  );
}

export function MapSelectionSummaryStrip({
  selectedDevices = [],
  getDeviceType,
  showNodeCapBreakdown = true,
}) {
  const nodes = showNodeCapBreakdown && getDeviceType
    ? selectedDevices.filter((d) => getDeviceType(d.capabilities) === 'Node').length
    : null;
  const caps = showNodeCapBreakdown && getDeviceType
    ? selectedDevices.filter((d) => getDeviceType(d.capabilities) === 'CAP').length
    : null;
  const online = selectedDevices.filter((d) => d.connected).length;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        px: 1.5,
        py: 1.25,
        mb: 2.5,
        borderRadius: 2,
        bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'),
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
        Selection
      </Typography>
      <Chip label={`${selectedDevices.length} total`} size="small" color="primary" variant="outlined" />
      {nodes != null && <Chip label={`${nodes} nodes`} size="small" variant="outlined" />}
      {caps != null && <Chip label={`${caps} CAPs`} size="small" variant="outlined" />}
      <Chip label={`${online} online`} size="small" color="success" variant="outlined" />
    </Box>
  );
}

export function MapSelectionDialogBody({ children, minHeight = 360 }) {
  return (
    <DialogContent
      sx={{
        px: 3,
        py: 2.5,
        minHeight,
        bgcolor: 'background.paper',
      }}
    >
      {children}
    </DialogContent>
  );
}
